import {
  SKILL_NAMES,
  SkillId,
  VOCATION_NAMES,
  WEIGHT_SCALE,
  counted,
  staminaBand,
  type StatsPacket,
} from '@game/protocol';

import { collapsiblePanel, type PanelHeader } from './panel.ts';

export interface HudDiagnostics {
  connected: boolean;
  pingMs: number;
  tick: number;
  x: number;
  y: number;
  entities: number;
  corrections: number;
}

export interface HudTarget {
  id: number;
  name: string;
  health: number;
  maxHealth: number;
}

/**
 * Intervalo mínimo entre duas escritas da linha de diagnóstico, em ms. Ver `setDiagnostics`.
 *
 * Quatro por segundo: rápido o bastante para ping e tick servirem de diagnóstico, lento o
 * bastante para o texto parar de tremer. O `renderHud` que a chama roda a cada quadro.
 */
const DIAGNOSTICS_INTERVAL_MS = 250;

/**
 * Painel de status (PRD §6): vida, mana, nível, experiência e capacidade.
 *
 * Em DOM, pelo mesmo motivo da tela de login: texto e barras não precisam passar pelo
 * pipeline de render, e no DOM eles ficam legíveis em qualquer resolução sem uma fonte
 * de bitmap por tamanho.
 *
 * As barras são atualizadas por `style.width`, e os números por `textContent`, sempre
 * sobre os **mesmos** elementos. Reconstruir o HTML a cada quadro — sessenta vezes por
 * segundo — jogaria fora e recriaria dezenas de nós, e o navegador gastaria mais tempo
 * em layout do que o jogo inteiro gasta em render.
 */
export class Hud {
  private readonly root: HTMLElement;
  private readonly header: PanelHeader;

  private readonly healthFill: HTMLElement;
  private readonly healthText: HTMLElement;
  private readonly manaFill: HTMLElement;
  private readonly manaText: HTMLElement;
  private readonly expFill: HTMLElement;
  private readonly expText: HTMLElement;

  private readonly levelText: HTMLElement;
  private readonly vocationText: HTMLElement;
  private readonly placeText: HTMLElement;
  /** Saciedade e stamina na mesma linha (PRD §15.7). Ver `setStats`. */
  private readonly sustentoText: HTMLElement;
  private readonly capacityText: HTMLElement;

  private readonly skillList: HTMLElement;
  private readonly skillRows = new Map<SkillId, { level: HTMLElement; fill: HTMLElement }>();

  private readonly targetBox: HTMLElement;
  private readonly targetName: HTMLElement;
  private readonly targetFill: HTMLElement;

  private readonly diagnostics: HTMLElement;
  /** Última linha escrita, e quando. Ver `setDiagnostics`. */
  private diagnosticsText = '';
  private diagnosticsAtMs = Number.NEGATIVE_INFINITY;

  constructor(root: HTMLElement) {
    this.root = root;
    this.root.innerHTML = '';
    this.root.classList.add('hud');

    // O único painel da coluna que não fechava de jeito nenhum, e o mais alto dos quatro.
    // Recolher aqui é o que devolve as sete perícias e o diagnóstico em altura para quem está
    // com a loja e a lista de amigos abertas ao mesmo tempo (ver `panel.ts`).
    this.header = collapsiblePanel(this.root, 'status', 'Personagem');

    const identity = div('hud-identity');
    this.levelText = span('hud-level', 'nível 1');
    this.vocationText = span('hud-vocation', '');
    identity.append(this.levelText, this.vocationText);

    this.placeText = div('hud-place');

    const health = bar('vida', 'health');
    const mana = bar('mana', 'mana');
    const experience = bar('exp', 'exp');

    this.healthFill = health.fill;
    this.healthText = health.text;
    this.manaFill = mana.fill;
    this.manaText = mana.text;
    this.expFill = experience.fill;
    this.expText = experience.text;

    this.sustentoText = div('hud-sustento');
    this.capacityText = div('hud-capacity');
    this.skillList = div('hud-skills');

    this.targetBox = div('hud-target');
    this.targetName = span('hud-target-name', '');
    const targetBar = div('hud-bar target');
    this.targetFill = div('hud-bar-fill');
    targetBar.append(this.targetFill);
    this.targetBox.append(this.targetName, targetBar);
    this.targetBox.style.display = 'none';

    this.diagnostics = div('hud-diagnostics');

    // O sustento entra **antes** da capacidade, encostado nas três barras, e a ordem aqui
    // é decisão e não arrumação: o painel tem piso de altura e rola por dentro (ver
    // `--piso-status` em `style.css`), então quem está no fim é quem sai da vista quando a
    // coluna aperta. Saciedade e stamina explicam o que as barras logo acima fazem — fome
    // desliga a regeneração de vida e mana, a faixa de stamina multiplica a experiência
    // (PRD §15.7) —, enquanto capacidade é logística de mochila, consultada entre uma
    // caçada e outra. Com a ordem invertida, o primeiro item a sumir numa janela baixa
    // seria a única linha que responde "por que a minha vida parou de voltar?".
    this.root.append(
      this.header.root,
      identity,
      this.placeText,
      health.root,
      mana.root,
      experience.root,
      this.sustentoText,
      this.capacityText,
      this.skillList,
      this.targetBox,
      this.diagnostics,
    );

    this.buildSkillRows();
  }

  private buildSkillRows(): void {
    for (const skill of [
      SkillId.Fist,
      SkillId.Sword,
      SkillId.Axe,
      SkillId.Club,
      SkillId.Distance,
      SkillId.Shield,
      SkillId.Magic,
    ]) {
      const row = div('hud-skill');
      const name = span('hud-skill-name', SKILL_NAMES[skill]);
      const level = span('hud-skill-level', '10');

      const track = div('hud-skill-track');
      const fill = div('hud-skill-fill');
      track.append(fill);

      row.append(name, level, track);
      this.skillList.append(row);
      this.skillRows.set(skill, { level, fill });
    }
  }

  /** Atualiza o painel a partir do pacote `Stats`, que só chega quando algo muda. */
  setStats(stats: StatsPacket): void {
    this.levelText.textContent = `nível ${stats.level}`;
    this.vocationText.textContent = VOCATION_NAMES[stats.vocation] ?? '';

    setBar(this.healthFill, this.healthText, stats.health, stats.maxHealth);
    setBar(this.manaFill, this.manaText, stats.mana, stats.maxMana);

    // A barra de experiência mede o **trecho do nível**, não o total acumulado: uma
    // barra que anda de 0 a 100% em cada nível diz quanto falta; uma que mede o total
    // fica parada para sempre depois do nível 20.
    const span0 = stats.nextLevelExp - stats.levelStartExp;
    const done = stats.experience - stats.levelStartExp;
    const percent = span0 > 0 ? Math.max(0, Math.min(1, done / span0)) : 0;

    this.expFill.style.width = `${(percent * 100).toFixed(1)}%`;
    this.expText.textContent = `${Math.floor(percent * 100)}% · ${formatNumber(
      stats.nextLevelExp - stats.experience,
    )} para o nível ${stats.level + 1}`;

    // Sustento (PRD §15.7). Texto próprio, e não `setBar`: a barra escreve `valor/max`
    // cru, e os dois contadores são relógios em milissegundos — ela mostraria
    // `840000/1200000`, um par de números que ninguém converte de cabeça no meio de uma
    // caçada. Uma linha só para os dois porque são a mesma regra em duas escalas, e porque
    // cada linha deste painel custa altura de uma coluna que já está apertada.
    const band = staminaBand(stats.staminaMs);

    // Zero saciedade vira palavra, não número. `saciedade 0 min` seria lido como mais um
    // contador zerado; `com fome` é a **causa**, e é ela que faz o jogador procurar comida na
    // mochila em vez de procurar um bug na barra de vida. Casa com a frase que o servidor
    // manda no instante em que o relógio zera (`announceHunger`): quem entrou depois, ou
    // perdeu a linha no chat, encontra a mesma informação parada na tela.
    const fome = stats.foodMs > 0 ? `saciedade ${formatDuration(stats.foodMs)}` : 'com fome';

    // O multiplicador só aparece quando **não** é 1×, que é o estado da esmagadora maioria
    // das sessões: escrever "1× exp" ao lado de todo número transformaria a informação que
    // importa — a penalidade — em mais uma palavra que o olho aprende a pular.
    const fator =
      band.multiplier === 1 ? '' : `, ${band.multiplier.toLocaleString('pt-BR')}× exp`;

    // Na faixa de multiplicador 0 o rótulo sozinho não serve — "exausto" descreve o
    // personagem, e o que precisa ser dito é o que **deixou de acontecer**. Sem a frase, quem
    // caçou a noite inteira vê a barra de experiência parada com as criaturas morrendo
    // normalmente e conclui que o servidor perdeu a conta. Nada mais é tirado dele, e é por
    // isso que a linha fala de experiência e não de exaustão em geral.
    const descanso =
      band.multiplier === 0
        ? `stamina esgotada (${band.label}) — sem experiência`
        : `stamina ${formatDuration(stats.staminaMs)} (${band.label}${fator})`;

    this.sustentoText.textContent = `${fome} · ${descanso}`;

    this.capacityText.textContent = `capacidade ${formatWeight(
      stats.maxCapacity - stats.capacity,
    )} livres de ${formatWeight(stats.maxCapacity)}`;

    for (const [skill, row] of this.skillRows) {
      const state = stats.skills[skill];
      if (!state) continue;

      row.level.textContent = String(state.level);
      row.fill.style.width = `${state.percent}%`;
    }
  }

  setTarget(target: HudTarget | null): void {
    if (!target) {
      this.targetBox.style.display = 'none';
      return;
    }

    this.targetBox.style.display = '';
    this.targetName.textContent = `${target.name} — ${target.health}/${target.maxHealth}`;

    const ratio = target.maxHealth > 0 ? target.health / target.maxHealth : 0;
    this.targetFill.style.width = `${Math.max(0, Math.min(1, ratio)) * 100}%`;
    this.targetFill.style.background = healthColor(ratio);
  }

  /**
   * Onde o jogador está: região e andar.
   *
   * O andar só aparece quando não é o térreo. Um "andar 0" permanente na tela seria
   * ruído para os mapas planos, que são a maioria — e a informação só passa a importar
   * quando existe mais de um andar para confundir.
   *
   * O que é o térreo vem do servidor (`Welcome.groundFloor`) e **não é sempre o andar 0**:
   * `z` cresce para cima (PRD §3.5), então numa região com esgoto o subsolo é o 0 e a rua é
   * o 1, e numa caverna que desce a boca é o andar mais alto. Comparar contra zero anunciava
   * "andar 1" a quem estava na praça e nada a quem estava no esgoto.
   */
  setPlace(regionName: string, floor: number, groundFloor = 0): void {
    const depth = floor - groundFloor;

    if (depth === 0) {
      this.placeText.textContent = regionName;
      return;
    }

    this.placeText.textContent =
      depth > 0 ? `${regionName} · andar ${depth}` : `${regionName} · subsolo ${-depth}`;
  }

  /**
   * A linha de diagnóstico — e por que ela é a única do painel com relógio próprio.
   *
   * `renderHud` roda a **cada quadro**, e escrever `textContent` troca o nó de texto mesmo
   * quando a string é idêntica: medido no cliente publicado, com um `MutationObserver` sobre
   * a barra, dá **144 mutações por segundo** com o personagem parado, contra 1 a 9 de todo o
   * resto do painel junto. O navegador repinta aquele trecho sessenta vezes por segundo, e
   * como o `tick` muda vinte vezes nesse intervalo, os dígitos tremem — o defeito que o
   * jogador relatou como "o HUD do lado direito está tremido, não dá para mostrar no print".
   * Não sai no print porque não é geometria: a coluna não se move um pixel (medido em quatro
   * larguras, parado e andando). É cintilação de repintura.
   *
   * As duas guardas resolvem coisas diferentes e as duas são necessárias. O **intervalo**
   * corta a repintura de 60 para 4 por segundo, que é a resolução em que ping e tick são
   * legíveis — acima disso o número não é informação, é ruído. A **comparação** cobre o resto:
   * parado e conectado, a linha só muda quando o tick muda, e reescrever "o mesmo texto" é
   * exatamente o gasto que `markSustenanceDirty` já evita do lado do servidor.
   */
  setDiagnostics(state: HudDiagnostics): void {
    const agora = performance.now();
    if (agora - this.diagnosticsAtMs < DIAGNOSTICS_INTERVAL_MS) return;
    this.diagnosticsAtMs = agora;

    const texto = state.connected
      ? `ping ${state.pingMs}ms · tick ${state.tick} · ${state.x},${state.y} · ` +
        `${state.entities} em cena · ${counted(state.corrections, 'correção', 'correções')}`
      : 'desconectado';

    if (texto === this.diagnosticsText) return;

    this.diagnosticsText = texto;
    this.diagnostics.textContent = texto;
    this.diagnostics.classList.toggle('offline', !state.connected);
  }
}

// --- Auxiliares -------------------------------------------------------------

function div(className: string): HTMLDivElement {
  const node = document.createElement('div');
  node.className = className;
  return node;
}

function span(className: string, text: string): HTMLSpanElement {
  const node = document.createElement('span');
  node.className = className;
  node.textContent = text;
  return node;
}

function bar(
  label: string,
  variant: string,
): { root: HTMLElement; fill: HTMLElement; text: HTMLElement } {
  const root = div('hud-bar-row');
  const caption = span('hud-bar-label', label);

  const track = div(`hud-bar ${variant}`);
  const fill = div('hud-bar-fill');
  track.append(fill);

  const text = span('hud-bar-text', '');
  root.append(caption, track, text);

  return { root, fill, text };
}

function setBar(
  fill: HTMLElement,
  text: HTMLElement,
  value: number,
  max: number,
): void {
  const ratio = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0;
  fill.style.width = `${(ratio * 100).toFixed(1)}%`;
  text.textContent = `${value}/${max}`;
}

/** Verde acima de 50%, âmbar acima de 20%, vermelho abaixo. */
function healthColor(ratio: number): string {
  if (ratio > 0.5) return '#5cb85c';
  if (ratio > 0.2) return '#e0a33e';
  return '#d9534f';
}

function formatWeight(units: number): string {
  return `${(units / WEIGHT_SCALE).toFixed(1)} oz`;
}

/**
 * Duração em ms para texto curto: `18 min` abaixo de uma hora, `41h20` daí para cima.
 *
 * É o **primeiro formatador de tempo do cliente** — até o sustento (PRD §15.7) nada na tela
 * era medido em relógio, só em pontos, peso e porcentagem —, e por isso vale dizer onde ele
 * mora e por quê. Fica aqui, e não em `@game/protocol`, porque o protocolo transporta os
 * milissegundos e é a interface que escolhe a unidade que cabe na linha; a mesma duração vira
 * outra coisa numa janela de chat ou num tooltip. Quando o segundo aparecer — recarga de
 * magia, tempo de respawn —, é daqui que ele sai, e não de uma segunda cópia desta conta.
 *
 * Arredonda para **cima**, e isso não é gosto: é a mesma resolução em que o servidor decide
 * reenviar o `Stats` (`markSustenanceDirty` compara `Math.ceil(ms / 60000)`). Arredondando
 * para baixo, o número da tela mudaria um minuto depois do pacote que o trouxe, e os últimos
 * cinquenta e nove segundos de saciedade apareceriam como `0 min` — o jogador leria fome onde
 * ainda há comida no relógio, e correria para comer o que não precisava.
 *
 * As duas escalas na mesma função porque as duas grandezas atravessam a fronteira: a
 * saciedade nasce em vinte minutos e a stamina em quarenta e duas horas, mas a stamina
 * **termina** em minutos, e é justamente aí que ela importa. `0h07` é o mesmo número na
 * escala errada — quem está no fim da stamina conta minutos, e a hora que sobra na frente é
 * um zero que ele precisa atravessar para chegar ao que interessa.
 */
function formatDuration(ms: number): string {
  // Piso em zero em vez de confiar no campo: `foodMs` e `staminaMs` chegam como `u32` e não
  // podem ser negativos hoje, mas um `-1 min` escapando para a tela seria lido como fome
  // por quem ainda tinha comida — barato demais para deixar depender de um tipo do outro lado.
  const minutes = Math.max(0, Math.ceil(ms / 60_000));
  if (minutes < 60) return `${minutes} min`;

  // `padStart` para que `41h05` não vire `41h5`: em fonte monoespaçada o número sem o zero
  // desalinha a linha inteira a cada minuto que passa, e o painel pisca sozinho.
  return `${Math.floor(minutes / 60)}h${String(minutes % 60).padStart(2, '0')}`;
}

function formatNumber(value: number): string {
  return Math.max(0, Math.round(value)).toLocaleString('pt-BR');
}
